import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { topSpeciesByOccurrence } from './data/catalogData';

interface SpeciesOccurrenceItem {
  name: string;
  count: number;
}

interface MostFrequentSpeciesCardProps {
  /** Optional real detected species with total occurrences */
  realSpecies?: Array<{ name: string; total: number }>;
}

const DEFAULT_TOP_SPECIES: SpeciesOccurrenceItem[] = topSpeciesByOccurrence(10);

const MostFrequentSpeciesCard: React.FC<MostFrequentSpeciesCardProps> = ({ realSpecies }) => {
  const topSpecies = useMemo<SpeciesOccurrenceItem[]>(() => {
    if (realSpecies && realSpecies.length >= 5) {
      const sorted = [...realSpecies]
        .filter((s) => s.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, 10)
        .map((s) => ({ name: s.name, count: s.total }));
      if (sorted.length > 0) return sorted;
    }
    return DEFAULT_TOP_SPECIES;
  }, [realSpecies]);

  const maxCount = useMemo(() => {
    return topSpecies.length > 0 ? Math.max(...topSpecies.map((s) => s.count)) : 1;
  }, [topSpecies]);

  return (
    <Box
      sx={{
        background: 'linear-gradient(180deg, rgba(18, 24, 21, 0.85) 0%, rgba(10, 14, 12, 0.95) 100%)',
        border: '1px solid #1E2E25',
        borderRadius: '12px',
        p: { xs: 2, md: 2.5 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        minHeight: '460px',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: '10px',
            color: '#10B981',
            fontWeight: 800,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            mb: 0.5,
          }}
        >
          OCCURRENCE
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '16px', md: '18px' },
            fontWeight: 700,
            color: '#FFFFFF',
            lineHeight: 1.2,
          }}
        >
          Most frequently detected species
        </Typography>
        <Typography sx={{ fontSize: '11px', color: '#888888', mt: 0.5 }}>
          Avg. daily occurrence, combined across deployments with activity data
        </Typography>
      </Box>

      {/* Top 10 Ranked List */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          flex: 1,
          gap: 1,
        }}
      >
        {topSpecies.map((item, index) => {
          const rankStr = String(index + 1).padStart(2, '0');
          const percentage = Math.max(3, (item.count / maxCount) * 100);

          return (
            <Box
              key={item.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 0.5,
                transition: 'background-color 0.15s ease',
                borderRadius: '6px',
                px: 0.5,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.02)',
                },
              }}
            >
              {/* Rank */}
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#666666',
                  width: '26px',
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {rankStr}
              </Typography>

              {/* Species Name */}
              <Typography
                sx={{
                  fontSize: '12.5px',
                  fontWeight: 600,
                  color: '#EDEDED',
                  width: { xs: '130px', sm: '160px', md: '175px' },
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  pr: 1.5,
                }}
                title={item.name}
              >
                {item.name}
              </Typography>

              {/* Proportional Gradient Bar */}
              <Box
                sx={{
                  flex: 1,
                  height: '7px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '999px',
                  mx: 1.5,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${percentage}%`,
                    background: 'linear-gradient(90deg, #0D9488 0%, #10B981 100%)',
                    borderRadius: '999px',
                    transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              </Box>

              {/* Occurrence Count */}
              <Typography
                sx={{
                  fontSize: '12.5px',
                  fontWeight: 700,
                  color: '#FFFFFF',
                  minWidth: '60px',
                  textAlign: 'right',
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {item.count.toLocaleString()}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MostFrequentSpeciesCard;
